'use client';
/**
 * LES MISES EN SITUATION — la banque et le passage (H1, 26/08/2026).
 * ──────────────────────────────────────────────────────────────────
 * « Un outil de cas cliniques pour biologistes et techniciens : QCM, résultat
 * immédiat, validé/échec selon un taux minimal fixé. Chaque évaluation entre au
 * dossier de la personne. Les mises en situation vivent dans une banque
 * consultable. » (RQ)
 *
 * ── Ce que l'écran REFUSE de faire ────────────────────────────────────────
 *
 *  · Corriger lui-même. Les bonnes réponses n'arrivent JAMAIS dans la page
 *    avant la soumission : la fiche servie est sans corrigé, la note vient du
 *    serveur. Un QCM dont le corrigé voyage dans la page évalue l'ouverture de
 *    l'inspecteur du navigateur.
 *  · Laisser passer un brouillon. Le contenu clinique rédigé par l'assistant
 *    attend la relecture de la RQ, cas par cas — l'écran le dit, et le serveur
 *    le refuse de toute façon.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { I, card, EmptyState, Skeleton, FilterBar, RefISO, COUCHES } from '@/components/ui';
import { H1 } from './ressources/commun';
import { fmtDate } from '@/lib/meta';

import { Bouton } from '@/shared/Bouton';
interface Ligne {
  documentId: string; titre: string; discipline: string; publicCible: string;
  contexte: string; nbQuestions: number; seuilPourcent: number; statut: string;
  mesPassages: number;
  dernier: { pourcent: number; reussi: boolean; passeLe: string } | null;
}
interface QuestionPub { question: string; options: string[]; multiple: boolean }
interface Correction { cochees: number[]; bonnes: number[]; exacte: boolean; explication: string }
interface Resultat { score: number; total: number; pourcent: number; seuil: number; reussi: boolean; corrections: Correction[]; phrase: string }

/** Le passage d'un cas : questions sans corrigé, soumission, correction. */
function Passage({ id, onFerme, onFini }: { id: string; onFerme: () => void; onFini: () => void }) {
  const [fiche, setFiche] = useState<{ titre: string; contexte: string; seuilPourcent: number; questions: QuestionPub[] } | null>(null);
  const [coches, setCoches] = useState<Record<number, Set<number>>>({});
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/situations/${id}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setFiche(j); })
      .catch(() => undefined);
    return () => { vivant = false; };
  }, [id]);

  const cocher = (q: number, o: number, multiple: boolean) => {
    setCoches((c) => {
      const n = { ...c };
      const s = new Set(multiple ? (n[q] || []) : []);
      if (s.has(o)) s.delete(o); else s.add(o);
      n[q] = s;
      return n;
    });
  };

  const soumettre = async () => {
    if (!fiche) return;
    setEnCours(true);
    try {
      const reponses = fiche.questions.map((_, i) => [...(coches[i] || [])].sort((a, b) => a - b));
      const r = await fetchEcriture(`/api/ged/ged/situations/${id}/repondre`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reponses }),
      });
      const j = await r.json().catch(() => null);
      if (r.ok && j?.ok) { setResultat(j); onFini(); }
    } finally { setEnCours(false); }
  };

  const repondues = fiche ? fiche.questions.filter((_, i) => (coches[i]?.size || 0) > 0).length : 0;

  return (
    <div role="dialog" aria-modal="true" aria-label={fiche?.titre || 'Mise en situation'}
      style={{ position: 'fixed', inset: 0, zIndex: COUCHES.PLEIN_ECRAN, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div style={{ ...card, padding: 20, maxWidth: 720, width: '100%', maxHeight: '88vh', overflowY: 'auto', display: 'grid', gap: 14 }}>
        {!fiche ? <Skeleton rows={4} /> : (
          <>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 16, color: 'var(--text-strong)' }}>{fiche.titre}</h2>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>seuil de réussite : {fiche.seuilPourcent} %</span>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" type="button" onClick={onFerme}>
                {resultat ? 'Fermer' : 'Abandonner'}
              </Bouton>
            </div>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: 'var(--text-body)', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '10px 14px' }}>
              {fiche.contexte}
            </p>

            {fiche.questions.map((q, i) => {
              const corr = resultat?.corrections[i];
              return (
                <div key={i} style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>
                    {i + 1}. {q.question}
                    {q.multiple && <span style={{ fontWeight: 400, fontSize: 12.5, color: 'var(--text-muted)' }}> — plusieurs réponses</span>}
                  </div>
                  <div style={{ display: 'grid', gap: 4 }}>
                    {q.options.map((o, j) => {
                      const cochee = coches[i]?.has(j) || false;
                      const bonne = corr?.bonnes.includes(j) || false;
                      return (
                        <label key={j} style={{
                          display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.5,
                          padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                          cursor: resultat ? 'default' : 'pointer',
                          background: corr ? (bonne ? 'var(--green-50)' : cochee ? 'var(--red-50)' : undefined)
                            : cochee ? 'var(--cyan-50)' : undefined,
                          border: `1px solid ${corr && bonne ? 'var(--green-600)' : cochee ? 'var(--cyan-200)' : 'var(--border-subtle)'}`,
                        }}>
                          <input type={q.multiple ? 'checkbox' : 'radio'} name={`q${i}`} checked={cochee}
                            disabled={!!resultat} onChange={() => cocher(i, j, q.multiple)}
                            style={{ accentColor: 'var(--color-primary)', marginTop: 2 }} />
                          <span>{o}</span>
                        </label>
                      );
                    })}
                  </div>
                  {corr && (
                    <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: corr.exacte ? 'var(--green-600)' : 'var(--red-600)' }}>
                      {corr.exacte ? '✓ Exact. ' : '✗ Inexact. '}
                      <span style={{ color: 'var(--text-muted)' }}>{corr.explication}</span>
                    </p>
                  )}
                </div>
              );
            })}

            {resultat ? (
              <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: 13.5, fontWeight: 600,
                background: resultat.reussi ? 'var(--green-50)' : 'var(--red-50)',
                color: resultat.reussi ? 'var(--green-600)' : 'var(--red-600)' }}>
                {resultat.phrase}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{repondues}/{fiche.questions.length} répondues</span>
                <span style={{ flex: 1 }} />
                {/* ⚠️ On peut soumettre incomplet — une question sans réponse est
                    comptée FAUSSE, et l'écran le dit plutôt que de bloquer. */}
                {repondues < fiche.questions.length && repondues > 0 && (
                  <span style={{ fontSize: 12.5, color: 'var(--amber-600)' }}>les questions sans réponse compteront fausses</span>
                )}
                <Bouton variante="primaire" type="button" disabled={enCours || repondues === 0} onClick={() => void soumettre()}>
                  Soumettre — résultat immédiat
                </Bouton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** La relecture RQ : la fiche COMPLÈTE, corrigé compris, puis « Valider ». */
function Relecture({ id, onFerme, onValide }: { id: string; onFerme: () => void; onValide: () => void }) {
  const [fiche, setFiche] = useState<Ligne & { questions: { question: string; options: string[]; bonnes: number[]; explication?: string }[] } | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/situations/${id}?relecture=1`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setFiche(j); })
      .catch(() => undefined);
    return () => { vivant = false; };
  }, [id]);

  const valider = async () => {
    setEnCours(true);
    try {
      /* La version RELUE voyage avec la validation (14/10/2026) : modifiée
         depuis son affichage, le moteur refuse au lieu de valider à l'aveugle. */
      const r = await fetchEcriture(`/api/ged/ged/situations/${id}/valider`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updatedAt: (fiche as { updatedAt?: string } | null)?.updatedAt ?? null }),
      });
      if (r.ok) { onValide(); onFerme(); }
    } finally { setEnCours(false); }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Relecture"
      style={{ position: 'fixed', inset: 0, zIndex: COUCHES.PLEIN_ECRAN, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div style={{ ...card, padding: 20, maxWidth: 760, width: '100%', maxHeight: '88vh', overflowY: 'auto', display: 'grid', gap: 12 }}>
        {!fiche ? <Skeleton rows={4} /> : (
          <>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>{fiche.titre}</h2>
              <span style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 700 }}>BROUILLON — relecture avec corrigé</span>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" type="button" onClick={onFerme}>Fermer</Bouton>
            </div>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '10px 14px' }}>{fiche.contexte}</p>
            {fiche.questions.map((q, i) => (
              <div key={i} style={{ fontSize: 12.5, lineHeight: 1.55 }}>
                <strong>{i + 1}. {q.question}</strong>
                <ul style={{ margin: '4px 0', paddingInlineStart: 20 }}>
                  {q.options.map((o, j) => (
                    <li key={j} style={{ color: q.bonnes.includes(j) ? 'var(--green-600)' : undefined, fontWeight: q.bonnes.includes(j) ? 600 : 400 }}>
                      {o}{q.bonnes.includes(j) ? ' ✓' : ''}
                    </li>
                  ))}
                </ul>
                {q.explication && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>{q.explication}</p>}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Bouton variante="primaire" type="button" disabled={enCours} onClick={() => void valider()}>
                J’ai relu — valider cette mise en situation
              </Bouton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type QEdit = { question: string; options: string[]; bonnes: number[]; explication: string };

const VIDE_Q = (): QEdit => ({ question: '', options: ['', ''], bonnes: [], explication: '' });

/**
 * ÉCRIRE OU MODIFIER UN CAS (demande d'Inès du 27/08/2026).
 *
 * ⚠️ TOUTE MODIFICATION RENVOIE AU BROUILLON, et l'écran le dit AVANT le clic.
 * Le moteur l'applique de toute façon ; l'annoncer évite qu'on découvre après
 * coup qu'un cas validé est redescendu — et qu'on croie à un bug plutôt qu'à
 * une règle. Rééditer, c'est redemander une relecture : sans cela, la
 * validation continuerait de couvrir un contenu qu'elle n'a jamais vu.
 */
function Editeur({ id, onFerme, onEnregistre }: { id: string; onFerme: () => void; onEnregistre: () => void }) {
  const neuf = id === 'neuf';
  const [f, setF] = useState({ titre: '', discipline: '', publicCible: 'technicien', contexte: '', seuilPourcent: 80 });
  const [qs, setQs] = useState<QEdit[]>([VIDE_Q()]);
  /* Ce que l'éditeur a CHARGÉ, figé une fois (14/10/2026), sous la forme
     exacte du corps envoyé : le moteur n'écrit que ce qui a changé depuis. */
  const [base, setBase] = useState<Record<string, unknown> | null>(null);
  const [etaitValide, setEtaitValide] = useState(false);
  const [pret, setPret] = useState(neuf);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (neuf) return undefined;
    let vivant = true;
    void fetch(`/api/ged/ged/situations/${id}?relecture=1`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!vivant || !j) return;
        setF({
          titre: j.titre || '', discipline: j.discipline || '', publicCible: j.publicCible || 'technicien',
          contexte: j.contexte || '', seuilPourcent: j.seuilPourcent || 80,
        });
        setQs((j.questions || []).map((q: QEdit) => ({
          question: q.question || '', options: q.options?.length ? q.options : ['', ''],
          bonnes: q.bonnes || [], explication: q.explication || '',
        })));
        setBase((b) => b ?? {
          titre: j.titre || '', discipline: j.discipline || '', publicCible: j.publicCible || 'technicien',
          contexte: j.contexte || '', seuilPourcent: j.seuilPourcent || 80,
          questions: (j.questions || []).map((q: QEdit) => ({
            question: String(q.question || '').trim(),
            options: (q.options || []).map((o) => String(o).trim()).filter(Boolean),
            bonnes: q.bonnes || [],
            explication: String(q.explication || '').trim(),
          })),
        });
        setEtaitValide(j.statut === 'valide');
        setPret(true);
      })
      .catch(() => undefined);
    return () => { vivant = false; };
  }, [id, neuf]);

  const majQ = (i: number, p: Partial<QEdit>) => setQs((l) => l.map((q, j) => (j === i ? { ...q, ...p } : q)));
  /* ⚠️ Retirer une option DÉCALE les indices des bonnes réponses : sans ce
     recalcul, le corrigé désignerait silencieusement une autre option — le
     candidat serait noté contre une réponse que personne n'a voulue. */
  const retirerOption = (i: number, j: number) => setQs((l) => l.map((q, k) => {
    if (k !== i) return q;
    return {
      ...q,
      options: q.options.filter((_, x) => x !== j),
      bonnes: q.bonnes.filter((b) => b !== j).map((b) => (b > j ? b - 1 : b)),
    };
  }));

  const enregistrer = async () => {
    setErreur(''); setEnCours(true);
    try {
      const corps = {
        ...f,
        questions: qs.map((q) => ({
          question: q.question.trim(),
          options: q.options.map((o) => o.trim()).filter(Boolean),
          bonnes: q.bonnes,
          explication: q.explication.trim(),
        })),
        ...(!neuf && base ? { base } : {}),
      };
      const r = await fetchEcriture(neuf ? '/api/ged/ged/situations' : `/api/ged/ged/situations/${id}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
      });
      /* ⚠️ On LIT le message du moteur. Il nomme le défaut précis — « question 2 :
         deux options au moins », « une bonne réponse pointe hors des options » —
         et c'est tout l'intérêt de la vérification à l'écriture. Afficher
         « refusé » à la place rendrait la garde inutilisable. */
      if (!r.ok) {
        const b = await r.json().catch(() => null);
        setErreur(b?.error?.message || 'Enregistrement refusé.');
        return;
      }
      onEnregistre(); onFerme();
    } finally { setEnCours(false); }
  };

  const lbl2 = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 3 };
  const champ = { width: '100%', padding: '7px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-sans)', fontSize: 13 };

  return (
    <div role="dialog" aria-modal="true" aria-label={neuf ? 'Écrire un cas' : 'Modifier le cas'}
      style={{ position: 'fixed', inset: 0, zIndex: COUCHES.PLEIN_ECRAN, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div style={{ ...card, padding: 20, maxWidth: 820, width: '100%', maxHeight: '90vh', overflowY: 'auto', display: 'grid', gap: 14 }}>
        {!pret ? <Skeleton rows={5} /> : (
          <>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 16 }}>{neuf ? 'Écrire une mise en situation' : 'Modifier la mise en situation'}</h2>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" type="button" onClick={onFerme}>Fermer</Bouton>
            </div>

            {etaitValide && (
              <div style={{ fontSize: 12.5, lineHeight: 1.55, background: 'var(--amber-50)', border: '1px solid var(--amber-300)', borderRadius: 'var(--radius-sm)', padding: '9px 13px' }}>
                Ce cas est <strong>validé</strong>. L’enregistrer le fera <strong>retourner au brouillon</strong> :
                une validation ne peut pas couvrir un contenu qu’elle n’a pas relu. Il faudra le revalider.
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 10 }}>
              <div><label style={lbl2} htmlFor="s-titre">Intitulé *</label>
                <input id="s-titre" value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })} style={champ} /></div>
              <div><label style={lbl2} htmlFor="s-disc">Discipline</label>
                <input id="s-disc" value={f.discipline} onChange={(e) => setF({ ...f, discipline: e.target.value })} style={champ} /></div>
              <div><label style={lbl2} htmlFor="s-pub">Public</label>
                <select id="s-pub" value={f.publicCible} onChange={(e) => setF({ ...f, publicCible: e.target.value })} style={champ}>
                  <option value="technicien">Côté technicien</option>
                  <option value="biologiste">Côté biologiste</option>
                </select></div>
              <div><label style={lbl2} htmlFor="s-seuil">Seuil de réussite (%)</label>
                <input id="s-seuil" type="number" min={1} max={100} value={f.seuilPourcent}
                  onChange={(e) => setF({ ...f, seuilPourcent: +e.target.value || 80 })} style={champ} /></div>
            </div>
            <div><label style={lbl2} htmlFor="s-ctx">Contexte clinique</label>
              <textarea id="s-ctx" rows={3} value={f.contexte} onChange={(e) => setF({ ...f, contexte: e.target.value })}
                style={{ ...champ, resize: 'vertical' }} /></div>

            {qs.map((q, i) => (
              <div key={i} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <strong style={{ fontSize: 12.5 }}>Question {i + 1}</strong>
                  <span style={{ flex: 1 }} />
                  {qs.length > 1 && (
                    <Bouton taille="sm" type="button" onClick={() => setQs((l) => l.filter((_, j) => j !== i))}>Retirer la question</Bouton>
                  )}
                </div>
                <input value={q.question} onChange={(e) => majQ(i, { question: e.target.value })}
                  placeholder="L’énoncé" style={champ} />
                {q.options.map((o, j) => (
                  <div key={j} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {/* La case dit « c’est une bonne réponse ». Plusieurs sont
                        permises : la correction est en tout-ou-rien. */}
                    <input type="checkbox" checked={q.bonnes.includes(j)} aria-label={`Option ${j + 1} : bonne réponse`}
                      onChange={(e) => majQ(i, {
                        bonnes: e.target.checked ? [...q.bonnes, j] : q.bonnes.filter((b) => b !== j),
                      })} />
                    <input value={o} placeholder={`Option ${j + 1}`} style={{ ...champ, flex: 1 }}
                      onChange={(e) => majQ(i, { options: q.options.map((x, k) => (k === j ? e.target.value : x)) })} />
                    {q.options.length > 2 && (
                      <Bouton taille="sm" type="button" onClick={() => retirerOption(i, j)} aria-label={`Retirer l’option ${j + 1}`}>×</Bouton>
                    )}
                  </div>
                ))}
                <Bouton taille="sm" type="button" onClick={() => majQ(i, { options: [...q.options, ''] })} style={{ width: 'fit-content' }}>+ une option</Bouton>
                <input value={q.explication} onChange={(e) => majQ(i, { explication: e.target.value })}
                  placeholder="Explication montrée après la correction" style={champ} />
              </div>
            ))}
            <Bouton taille="sm" type="button" onClick={() => setQs((l) => [...l, VIDE_Q()])} style={{ width: 'fit-content' }}>+ une question</Bouton>

            {erreur && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Bouton taille="sm" type="button" onClick={onFerme}>Revenir</Bouton>
              {/* ⚠️ Jamais grisé sur un champ manquant : le moteur vérifie la
                  banque et NOMME le défaut (« question 2 : deux options au
                  moins »). Un bouton éteint ne dit rien de ce qui manque. */}
              <Bouton variante="primaire" type="button" disabled={enCours} onClick={() => void enregistrer()}>
                {enCours ? 'Enregistrement…' : 'Enregistrer — en brouillon'}
              </Bouton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * RETIRER de la banque — pas supprimer.
 *
 * ⚠️ Un cas qui a été PASSÉ est adossé à des résultats nominatifs. L'effacer
 * laisserait des notes sans savoir sur quoi elles portent. La fenêtre annonce
 * donc ce qui est préservé, avant le clic.
 */
function Retrait({ id, titre, motif, onMotif, onFerme, onRetire }: {
  id: string; titre: string; motif: string;
  onMotif: (v: string) => void; onFerme: () => void; onRetire: () => void;
}) {
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const assez = motif.trim().length >= 20;

  const retirer = async () => {
    setErreur(''); setEnCours(true);
    try {
      const r = await fetchEcriture(`/api/ged/ged/situations/${id}/retirer`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ motif: motif.trim() }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => null);
        setErreur(b?.error?.message || 'Retrait refusé.');
        return;
      }
      onRetire(); onFerme();
    } finally { setEnCours(false); }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Retirer de la banque"
      style={{ position: 'fixed', inset: 0, zIndex: COUCHES.PLEIN_ECRAN, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div style={{ ...card, padding: 20, maxWidth: 560, width: '100%', display: 'grid', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Retirer « {titre} »</h2>
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55 }}>
          Le cas sort de la banque : il ne pourra plus être passé. <strong>Les passages déjà joués sont
          conservés</strong> — ils disent ce que les gens ont obtenu, et sur quoi. Rien n’est supprimé,
          et la décision est portée au journal.
        </p>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 3 }} htmlFor="s-motif">
            Motif du retrait *
          </label>
          <textarea id="s-motif" rows={3} value={motif} onChange={(e) => onMotif(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-sans)', fontSize: 13, resize: 'vertical' }} />
          <div style={{ fontSize: 12.5, color: assez ? 'var(--text-muted)' : 'var(--amber-600)', marginTop: 3 }}>
            {assez
              ? 'Ce motif sera relu dans six mois : il doit se suffire à lui-même.'
              : `Au moins 20 caractères (${motif.trim().length}). « Erreur » n’explique rien.`}
          </div>
        </div>
        {erreur && <p style={{ margin: 0, fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</p>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Bouton taille="sm" type="button" onClick={onFerme}>Revenir</Bouton>
          <Bouton variante="primaire" type="button" disabled={!assez || enCours} onClick={() => void retirer()}>
            Confirmer le retrait
          </Bouton>
        </div>
      </div>
    </div>
  );
}

export function Situations() {
  const session = useGed((s) => s.session);
  const [lignes, setLignes] = useState<Ligne[] | null>(null);
  const [estRq, setEstRq] = useState(false);
  /** `'neuf'` = création ; un documentId = modification. */
  const [edite, setEdite] = useState<string | null>(null);
  const [retire, setRetire] = useState<string | null>(null);
  const [motifRetrait, setMotifRetrait] = useState('');
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [passe, setPasse] = useState<string | null>(null);
  const [relit, setRelit] = useState<string | null>(null);
  const [tour, setTour] = useState(0);

  const recharger = useCallback(() => setTour((t) => t + 1), []);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/situations', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant && j) { setLignes(j.situations || []); setEstRq(!!j.estRq); } })
      .catch(() => { if (vivant) setLignes([]); });
    return () => { vivant = false; };
  }, [tour]);

  const installer = async () => {
    await fetchEcriture('/api/ged/ged/situations/installer', { method: 'POST', credentials: 'include' }).catch(() => undefined);
    recharger();
  };

  const vues = useMemo(() => {
    const base = (lignes || []).filter((l) => (
      filtre === 'biologiste' ? l.publicCible === 'biologiste'
        : filtre === 'technicien' ? l.publicCible === 'technicien'
          : filtre === 'brouillons' ? l.statut !== 'valide'
            : true));
    const qq = q.trim().toLowerCase();
    return qq ? base.filter((l) => `${l.titre} ${l.discipline}`.toLowerCase().includes(qq)) : base;
  }, [lignes, filtre, q]);

  if (!session.roles.length) return null;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <H1 eyebrow={<>Personnel · Maintien des compétences · <RefISO code="6.2" /></>} title="Mises en situation" />
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.6, maxWidth: '92ch' }}>
        Des cas cliniques en QCM, par discipline, côté biologiste et côté technicien. Le résultat est
        immédiat, corrigé et expliqué question par question ; chaque passage entre au dossier, réussi
        ou non — repasser est toujours possible. {estRq && 'Un brouillon ne peut pas être passé : il attend votre relecture.'}
      </p>

      {estRq && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Bouton variante="primaire" type="button" onClick={() => setEdite('neuf')}>
            <I n="plus" s={14} /> Écrire un cas
          </Bouton>
          {lignes !== null && !lignes.length && (
            <Bouton taille="sm" type="button" onClick={() => void installer()}>
              Installer la banque de départ
            </Bouton>
          )}
        </div>
      )}

      {lignes !== null && lignes.length > 0 && (
        <FilterBar q={q} onQ={setQ} placeholder="Rechercher un cas, une discipline…"
          chips={[
            { k: 'tous', label: 'Tous', n: lignes.length },
            { k: 'technicien', label: 'Côté technicien', n: lignes.filter((l) => l.publicCible === 'technicien').length },
            { k: 'biologiste', label: 'Côté biologiste', n: lignes.filter((l) => l.publicCible === 'biologiste').length },
            ...(estRq ? [{ k: 'brouillons', label: 'À relire', n: lignes.filter((l) => l.statut !== 'valide').length }] : []),
          ]}
          actif={filtre} onChip={setFiltre}
          total={lignes.length} affiches={vues.length} onReset={() => { setQ(''); setFiltre('tous'); }} />
      )}

      {lignes === null ? (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={5} /></div>
      ) : !lignes.length ? (
        <EmptyState titre="La banque est vide"
          indice={estRq
            ? 'Installez la banque de départ : seize cas (huit disciplines, côté biologiste et côté technicien), en brouillon — chacun attend votre relecture avant de pouvoir être passé.'
            : 'Les mises en situation apparaîtront ici quand la RQ les aura publiées.'}
          action={estRq ? (
            <Bouton variante="primaire" type="button" onClick={() => void installer()}>
              <I n="plus" s={14} /> Installer la banque de départ
            </Bouton>
          ) : undefined} />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {vues.map((l) => (
            <div key={l.documentId} style={{ ...card, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 340px', minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13.5 }}>{l.titre}</strong>
                  {l.statut !== 'valide' && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--amber-600)', background: 'var(--amber-50)', padding: '1px 7px', borderRadius: 'var(--radius-pill)' }}>
                      brouillon — à relire
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                  {l.discipline} · {l.publicCible} · {l.nbQuestions} questions · seuil {l.seuilPourcent} %
                  {l.dernier && (
                    <> · dernier passage : <strong style={{ color: l.dernier.reussi ? 'var(--green-600)' : 'var(--red-600)' }}>
                      {l.dernier.pourcent} % {l.dernier.reussi ? '✓' : '✗'}
                    </strong> le {fmtDate(l.dernier.passeLe)}</>
                  )}
                </div>
              </div>
              {l.statut === 'valide' ? (
                <Bouton variante="primaire" taille="sm" type="button" onClick={() => setPasse(l.documentId)}>
                  {l.mesPassages ? 'Repasser' : 'Passer'}
                </Bouton>
              ) : estRq ? (
                <Bouton taille="sm" type="button" onClick={() => setRelit(l.documentId)}>
                  Relire et valider
                </Bouton>
              ) : (
                <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>en attente de relecture</span>
              )}
              {/* ⚠️ Les commandes d'écriture manquaient (demande d'Inès du
                  27/08/2026) : la banque ne s'alimentait que par « Installer »,
                  qui déverse un jeu écrit dans le code. La RQ pouvait relire et
                  valider — jamais écrire. */}
              {estRq && (
                <>
                  <Bouton taille="sm" type="button" onClick={() => setEdite(l.documentId)}>Modifier</Bouton>
                  <Bouton taille="sm" type="button" onClick={() => { setRetire(l.documentId); setMotifRetrait(''); }}>Retirer</Bouton>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {passe && <Passage id={passe} onFerme={() => setPasse(null)} onFini={recharger} />}
      {relit && <Relecture id={relit} onFerme={() => setRelit(null)} onValide={recharger} />}
      {edite && <Editeur id={edite} onFerme={() => setEdite(null)} onEnregistre={recharger} />}
      {retire && (
        <Retrait
          titre={lignes?.find((l) => l.documentId === retire)?.titre || ''}
          id={retire} motif={motifRetrait} onMotif={setMotifRetrait}
          onFerme={() => setRetire(null)} onRetire={recharger} />
      )}
    </div>
  );
}
